import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import moment from 'moment';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { Clickable } from '../../../../components/common/Clickable';
import { formatNumber, useTranslation } from '../../../../shared/i18n';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { appTheme, type ThemeColors } from '../../../../shared/theme';
import { DASH_STUDIO_INNER, DASH_STUDIO_RADIUS } from '../../../../shared/theme/studio/dashboardConstants';
import {
  useShowSubscriptionScaledMacrosEnabled,
  useCaloriesLimitVisible,
  usePortionTextMode,
  useShowPortionEnabled,
} from '../../../../data/domains/config';
import {
  findCatalogPlanForSubscription,
  formatMealDaysRemainingLabel,
  formatPersonCountLabel,
  getCatalogPlanPortion,
  resolveMergedSubscriptionImageUri,
  resolvePurchasedSubscriptionCardNutrition,
  resolveSubscriptionCardNutritionDisplay,
  useSubscriptionPlans,
  type SubscriptionData,
} from '../../../../data/domains/subscription';
import { SubscriptionPlanEdgeImage } from './SubscriptionPlanEdgeImage';
import type { FlatItem } from '../../DashboardMenuImagesStrip';

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const CARD_MIN_HEIGHT = 116;

function buildScaledOrPortionLine(
  display: ReturnType<typeof resolveSubscriptionCardNutritionDisplay>,
  t: (key: string, params?: Record<string, string | number>) => string,
  portion_text_mode: 'portion' | 'person',
): string | null {
  if (display.show_portion && display.fields.portion != null) {
    return formatPersonCountLabel(display.fields.portion, t, portion_text_mode);
  }
  if (!display.show_scaled_macros) {
    return null;
  }
  const parts: string[] = [];
  if (display.fields.scaled_protein != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_protein))}g P`);
  }
  if (display.fields.scaled_carbs != null) {
    parts.push(`${formatNumber(Math.round(display.fields.scaled_carbs))}g C`);
  }
  return parts.length > 0 ? parts.join('  ·  ') : null;
}

export interface DashboardSubscriptionCardSet2Props {
  colors: ThemeColors;
  subscription: SubscriptionData | null;
  subscription_loading: boolean;
  days_left: number;
  paused_days_left?: number;
  on_press: () => void;
  today_menu_strip_items?: FlatItem[];
}

export function DashboardSubscriptionCardSet2({
  colors,
  subscription,
  subscription_loading,
  days_left,
  paused_days_left = 0,
  on_press,
  today_menu_strip_items = [],
}: DashboardSubscriptionCardSet2Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const radius = DASH_STUDIO_RADIUS;
  const { data: catalog_plans } = useSubscriptionPlans();
  const show_scaled_macros_gate = useShowSubscriptionScaledMacrosEnabled();
  const calories_limit_gate = useCaloriesLimitVisible();
  const show_portion_gate = useShowPortionEnabled();
  const portion_text_mode = usePortionTextMode();

  const plan_title =
    subscription?.title ??
    subscription?.detail?.title ??
    (subscription as { name?: string } | null)?.name ??
    t('dashboard.myPlan');
  const start_date = subscription?.start_date ? moment(subscription.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true) : null;
  const end_date = subscription?.end_date ? moment(subscription.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true) : null;
  const date_range_label =
    start_date?.isValid() && end_date?.isValid()
      ? `${start_date.format('DD MMM YYYY')} - ${end_date.format('DD MMM YYYY')}`
      : '';

  const total_meals = subscription?.detail?.total_meals ?? 1;
  const total_snacks = subscription?.detail?.total_snacks ?? 0;

  const strip_uri = useMemo(() => {
    const with_uri = today_menu_strip_items.filter(i => i.uri);
    return with_uri[0]?.uri ?? null;
  }, [today_menu_strip_items]);

  const plan_uri = useMemo(() => {
    if (!subscription) {
      return null;
    }
    return resolveMergedSubscriptionImageUri(subscription, catalog_plans);
  }, [subscription, catalog_plans]);

  const primary_uri = plan_uri ?? strip_uri;
  const secondary_uri = plan_uri && strip_uri && plan_uri !== strip_uri ? strip_uri : null;

  const nutrition_display = useMemo(() => {
    if (!subscription) {
      return null;
    }
    const fields = resolvePurchasedSubscriptionCardNutrition(subscription);
    if (fields.portion == null && catalog_plans?.length) {
      const plan = findCatalogPlanForSubscription(subscription, catalog_plans);
      const catalog_portion = plan ? getCatalogPlanPortion(plan as Record<string, unknown>) : null;
      if (catalog_portion != null) {
        fields.portion = catalog_portion;
      }
    }
    return resolveSubscriptionCardNutritionDisplay(fields, {
      show_scaled_macros: show_scaled_macros_gate,
      calories_limit: calories_limit_gate,
      show_portion: show_portion_gate,
    });
  }, [subscription, catalog_plans, show_scaled_macros_gate, calories_limit_gate, show_portion_gate]);

  const portion_or_scaled_line = useMemo(() => {
    if (!nutrition_display) {
      return null;
    }
    return buildScaledOrPortionLine(nutrition_display, t, portion_text_mode);
  }, [nutrition_display, portion_text_mode, t]);

  const meals_snacks_label = `${formatNumber(total_meals)} ${t('dashboard.mealsLabel')}, ${formatNumber(total_snacks)} ${t('dashboard.snacksLabel')}`;
  const days_remaining_label = formatMealDaysRemainingLabel(days_left, paused_days_left, t);
  const image_fallback = <SvgIcon name="meal" size={36} color={colors.primary} />;

  const render_body = (body: React.ReactNode, a11y: string, image_primary: string | null, image_secondary: string | null) => (
    <Clickable onPress={on_press} accessibilityLabel={a11y} accessibilityRole="button">
      <View style={[styles.cardRoot, { backgroundColor: colors.surface }, rtl.flipRowStyle]}>
        <SubscriptionPlanEdgeImage
          uri={image_primary}
          fallback_uri={image_secondary}
          border_radius={radius}
          colors={colors}
          fallback={image_fallback}
          min_height={CARD_MIN_HEIGHT}
        />
        <View style={[styles.content, rtl.flipRowStyle]}>
          <View style={styles.contentText}>{body}</View>
          <View style={styles.chevron} pointerEvents="none">
            <SvgIcon name={rtl.trailingRowDisclosureChevron} size={appTheme.sizes.iconSizeMd} color={colors.textLight} />
          </View>
        </View>
      </View>
    </Clickable>
  );

  if (subscription_loading && !subscription) {
    return render_body(
      <>
        <Text style={[styles.title, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {t('dashboard.myPlan')}
        </Text>
        <Text style={[styles.line, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {t('common.loading')}
        </Text>
      </>,
      t('dashboard.myPlan'),
      strip_uri,
      null,
    );
  }

  if (!subscription) {
    return render_body(
      <>
        <Text style={[styles.title, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {t('dashboard.myPlan')}
        </Text>
        <Text
          style={[styles.line, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={2}>
          {t('subscriptions.noActivePlans')}
        </Text>
        <Text style={[styles.cta, { color: colors.primary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}>
          {t('dashboard.browsePlans')}
        </Text>
      </>,
      t('dashboard.browsePlans'),
      strip_uri,
      null,
    );
  }

  return render_body(
    <>
      <Text
        style={[styles.title, { color: colors.text, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
        numberOfLines={2}>
        {plan_title}
      </Text>
      {portion_or_scaled_line ? (
        <Text
          style={[styles.portionLine, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={1}>
          {portion_or_scaled_line}
        </Text>
      ) : null}
      <Text
        style={[styles.line, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
        numberOfLines={2}>
        {meals_snacks_label}
      </Text>
      <Text
        style={[styles.daysLine, { color: colors.primary, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
        numberOfLines={2}>
        {days_remaining_label}
      </Text>
      {date_range_label ? (
        <Text
          style={[styles.metaSmall, { color: colors.textLight, textAlign: text_align, writingDirection: writing_direction }, ANDROID_TEXT]}
          numberOfLines={2}>
          {date_range_label}
        </Text>
      ) : null}
    </>,
    plan_title,
    primary_uri,
    secondary_uri,
  );
}

const styles = StyleSheet.create({
  cardRoot: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: CARD_MIN_HEIGHT,
    borderRadius: DASH_STUDIO_RADIUS,
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: DASH_STUDIO_INNER,
    paddingStart: appTheme.spacing.md,
    paddingEnd: appTheme.spacing.sm,
    gap: appTheme.spacing.xs,
  },
  contentText: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  chevron: {
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
    paddingEnd: appTheme.spacing.xs,
  },
  title: {
    ...appTheme.typography.body,
    fontWeight: '800',
    fontSize: 15,
    lineHeight: 20,
    marginBottom: appTheme.spacing.xxs,
  },
  portionLine: {
    ...appTheme.typography.caption,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
    marginBottom: appTheme.spacing.xxs,
  },
  line: {
    ...appTheme.typography.caption,
    fontSize: 12,
    lineHeight: 16,
    marginBottom: appTheme.spacing.xxs,
  },
  daysLine: {
    ...appTheme.typography.caption,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
    marginBottom: appTheme.spacing.xxs,
  },
  metaSmall: {
    ...appTheme.typography.captionMini,
    fontSize: 11,
    marginTop: appTheme.spacing.xxs,
  },
  cta: {
    ...appTheme.typography.bodyMedium,
    marginTop: appTheme.spacing.xs,
    fontWeight: '700',
  },
});
